import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowBack, Save } from '@mui/icons-material';

const LegalDetail = ({ type, title }) => {
  const { id } = useParams();
  const isNew = id === 'new' || !id;
  const server_url = process.env.REACT_APP_API_URI;
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const editorElementRef = useRef(null);
  const toolbarContainerRef = useRef(null);
  const editorInitRef = useRef(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: title,
    body: '',
    version: '1.0',
    effectiveAt: '',
  });

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      setError('');
      setForm({
        title: title,
        body: '',
        version: '1.0',
        effectiveAt: '',
      });
      return;
    }
    const fetchItem = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(
          `${server_url}/admin/legal/${type}/${id}`,
        );
        const data = response.data?.data || null;
        setForm({
          title: data?.title || title,
          body: data?.body || '',
          version: data?.version || '1.0',
          effectiveAt: data?.effectiveAt
            ? new Date(data.effectiveAt).toISOString().slice(0, 16)
            : '',
        });
      } catch (err) {
        setError('Failed to load document.');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [id, isNew, server_url, title, type]);

  useEffect(() => {
    let isMounted = true;
    const initializeEditor = async () => {
      if (!isMounted || editorRef.current || editorInitRef.current) return;
      if (!window.DecoupledEditor || !editorElementRef.current) return;
      editorInitRef.current = true;
      try {
        const editor = await window.DecoupledEditor.create(
          editorElementRef.current,
        );
        if (!isMounted) {
          await editor.destroy();
          return;
        }
        if (toolbarContainerRef.current) {
          toolbarContainerRef.current.appendChild(
            editor.ui.view.toolbar.element,
          );
        }
        editor.setData(form.body || '');
        editor.model.document.on('change:data', () => {
          const data = editor.getData();
          setForm((prev) =>
            prev.body === data ? prev : { ...prev, body: data },
          );
        });
        editorRef.current = editor;
      } catch (err) {
        if (isMounted) {
          setError('Failed to load editor.');
        }
      }
    };

    const loadScript = () => {
      if (window.DecoupledEditor) {
        initializeEditor();
        return;
      }
      const existingScript = document.querySelector(
        'script[data-ckeditor="decoupled-document"]',
      );
      if (existingScript) {
        existingScript.addEventListener('load', initializeEditor, {
          once: true,
        });
        existingScript.addEventListener(
          'error',
          () => {
            if (isMounted) {
              setError('Failed to load editor.');
            }
          },
          { once: true },
        );
        return;
      }
      const script = document.createElement('script');
      script.src =
        'https://cdn.ckeditor.com/ckeditor5/41.4.2/decoupled-document/ckeditor.js';
      script.setAttribute('data-ckeditor', 'decoupled-document');
      script.onload = initializeEditor;
      script.onerror = () => {
        if (isMounted) {
          setError('Failed to load editor.');
        }
      };
      document.body.appendChild(script);
    };

    loadScript();

    return () => {
      isMounted = false;
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
      editorInitRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!editorRef.current) return;
    const current = editorRef.current.getData();
    if (form.body !== current) {
      editorRef.current.setData(form.body || '');
    }
  }, [form.body]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: form.title,
        body: form.body,
        version: form.version || undefined,
        effectiveAt: form.effectiveAt
          ? new Date(form.effectiveAt).toISOString()
          : undefined,
      };
      if (isNew) {
        await axios.post(`${server_url}/admin/legal/${type}`, payload);
      } else {
        await axios.put(`${server_url}/admin/legal/${type}/${id}`, payload);
      }
      navigate(`/admin/legal/${type}`);
    } catch (err) {
      setError('Failed to save document.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
            {isNew ? `Create ${title}` : `Edit ${title}`}
          </h1>
          <p className="text-gray-600">
            Manage policy content and publishing metadata
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/admin/legal/${type}`)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
        >
          <ArrowBack fontSize="small" />
          Back to list
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold text-gray-700">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(event) => handleChange('title', event.target.value)}
              className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={title}
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700">
              Version
            </label>
            <input
              type="text"
              value={form.version}
              onChange={(event) => handleChange('version', event.target.value)}
              className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="1.0"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700">
              Effective At
            </label>
            <input
              type="datetime-local"
              value={form.effectiveAt}
              onChange={(event) =>
                handleChange('effectiveAt', event.target.value)
              }
              className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-semibold text-gray-700">Body</label>
          <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden">
            <div
              ref={toolbarContainerRef}
              className="border-b border-gray-200 bg-gray-50"
            />
            <div
              ref={editorElementRef}
              className="min-h-[260px] p-4 bg-white"
            />
          </div>
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-70"
          >
            <Save fontSize="small" />
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LegalDetail;
